import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';

const TABS = [
  { key: 'editor', label: 'Dashboard notes', hint: 'Idea / issue / other via the feedback button' },
  { key: 'problem', label: 'Problem reports', hint: 'Inline "Report" on overview / article / summary' },
  { key: 'article', label: 'Article feedback', hint: 'Per-article Q1 / branch / followup answers' },
];

const GROUPINGS = [
  { key: 'editor', label: 'Editor' },
  { key: 'date', label: 'Date' },
  { key: 'none', label: 'None (flat)' },
];

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDate(isoDate) {
  if (!isoDate) return '—';
  // isoDate is "YYYY-MM-DD" — render in local order without timezone shift.
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function Pill({ tone = 'neutral', children }) {
  const tones = {
    neutral: 'bg-hxd-surface text-hxd-ink border-hxd-border',
    ok: 'bg-hxd-ok/10 text-hxd-ok border-hxd-ok/30',
    warn: 'bg-hxd-warn/10 text-hxd-warn border-hxd-warn/30',
    err: 'bg-red-50 text-hxd-err border-red-200',
    orange: 'bg-hxd-orange/10 text-hxd-orange border-hxd-orange/40',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 border rounded-[2px] px-1.5 py-0.5 font-display text-[9px] font-bold uppercase tracking-display-sm ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

function EditorBadge({ editor }) {
  if (!editor) return <span className="text-hxd-muted text-[12px]">unknown editor</span>;
  return (
    <span className="text-[12px] text-hxd-ink">
      <span className="font-semibold">{editor.name}</span>
      <span className="text-hxd-muted ml-1">· {editor.email}</span>
    </span>
  );
}

function ResolvedToggle({ resolved, busy, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      className={
        resolved
          ? 'hxd-btn hxd-btn-ghost hxd-btn-sm text-hxd-ok border-hxd-ok/40'
          : 'hxd-btn hxd-btn-ghost hxd-btn-sm'
      }
    >
      {busy ? '…' : resolved ? '✓ Resolved' : 'Mark resolved'}
    </button>
  );
}

// -------- Group / filter helpers --------

function groupRows(rows, grouping, getEditorKey, getDateKey) {
  if (grouping === 'none') return [{ key: 'all', label: 'All', rows }];
  const map = new Map();
  for (const r of rows) {
    const key = grouping === 'editor' ? getEditorKey(r) : getDateKey(r);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return [...map.entries()]
    .map(([key, groupRows]) => ({ key, label: key, rows: groupRows }))
    .sort((a, b) => (a.label < b.label ? 1 : -1));
}

function editorKey(r) {
  return r.editor?.name || r.editor?.email || 'Unknown editor';
}

function dateKey(r) {
  return r.isoDate || (r.createdAt ? r.createdAt.slice(0, 10) : 'undated');
}

// -------- Per-row renderers --------

function EditorFeedbackRow({ row, onToggleResolved, busy }) {
  const tone = row.type === 'issue' ? 'err' : row.type === 'idea' ? 'orange' : 'neutral';
  return (
    <div className="border-b border-hxd-border-soft last:border-0 py-3 px-4">
      <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={tone}>{row.type}</Pill>
          {row.resolved && <Pill tone="ok">Resolved</Pill>}
          <EditorBadge editor={row.editor} />
        </div>
        <div className="text-[11px] text-hxd-muted whitespace-nowrap">
          {fmtDateTime(row.createdAt)}
        </div>
      </div>
      <p className="text-[13px] text-hxd-ink whitespace-pre-wrap break-words">{row.message}</p>
      <div className="flex items-center justify-between gap-3 mt-2">
        <div className="text-[11px] text-hxd-muted">
          {row.view ? <>view: <span className="font-mono">{row.view}</span></> : null}
          {row.isoDate ? <span className="ml-3">day: {fmtDate(row.isoDate)}</span> : null}
        </div>
        <ResolvedToggle resolved={row.resolved} busy={busy} onToggle={onToggleResolved} />
      </div>
    </div>
  );
}

function ProblemReportRow({ row, onToggleResolved, busy }) {
  return (
    <div className="border-b border-hxd-border-soft last:border-0 py-3 px-4">
      <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone="warn">{row.category}</Pill>
          <Pill tone="neutral">{row.targetType}</Pill>
          {row.resolved && <Pill tone="ok">Resolved</Pill>}
          <EditorBadge editor={row.editor} />
        </div>
        <div className="text-[11px] text-hxd-muted whitespace-nowrap">
          {fmtDateTime(row.createdAt)}
        </div>
      </div>
      <p className="text-[13px] text-hxd-ink whitespace-pre-wrap break-words">{row.description}</p>
      <div className="flex items-center justify-between gap-3 mt-2">
        <div className="text-[11px] text-hxd-muted">
          day: {fmtDate(row.isoDate)}
          {row.targetId && (
            <span className="ml-3">target: <span className="font-mono">{row.targetId}</span></span>
          )}
        </div>
        <ResolvedToggle resolved={row.resolved} busy={busy} onToggle={onToggleResolved} />
      </div>
    </div>
  );
}

function ArticleFeedbackRow({ row }) {
  const branch = row.branch || row.q1Answer;
  return (
    <div className="border-b border-hxd-border-soft last:border-0 py-3 px-4">
      <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {branch && <Pill tone="orange">{branch}</Pill>}
          {row.toolUsed && <Pill tone="neutral">tool: {row.toolUsed}</Pill>}
          <EditorBadge editor={row.editor} />
        </div>
        <div className="text-[11px] text-hxd-muted whitespace-nowrap">
          {fmtDateTime(row.updatedAt || row.createdAt)}
        </div>
      </div>
      {row.article?.headline && (
        <p className="text-[13px] text-hxd-ink font-semibold mb-1">
          {row.article.headline}
        </p>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[12px]">
        {row.q1Label && (
          <>
            <dt className="text-hxd-muted">Q1</dt>
            <dd className="text-hxd-ink">{row.q1Label}</dd>
          </>
        )}
        {row.branchLabel && (
          <>
            <dt className="text-hxd-muted">Branch</dt>
            <dd className="text-hxd-ink">{row.branchLabel}</dd>
          </>
        )}
        {row.branchAnswer && (
          <>
            <dt className="text-hxd-muted">Branch answer</dt>
            <dd className="text-hxd-ink">{row.branchAnswer}</dd>
          </>
        )}
        {row.followupLabel && (
          <>
            <dt className="text-hxd-muted">Followup</dt>
            <dd className="text-hxd-ink">{row.followupLabel}</dd>
          </>
        )}
        {row.followupAnswer && (
          <>
            <dt className="text-hxd-muted">Followup answer</dt>
            <dd className="text-hxd-ink">{row.followupAnswer}</dd>
          </>
        )}
        {row.predictedFollowupBy && (
          <>
            <dt className="text-hxd-muted">Predicted by</dt>
            <dd className="text-hxd-ink">{row.predictedFollowupBy}</dd>
          </>
        )}
      </dl>
      <div className="text-[11px] text-hxd-muted mt-1.5">
        day: {fmtDate(row.isoDate)}
        <span className="ml-3">article: <span className="font-mono">{String(row.articleId).slice(-8)}</span></span>
      </div>
    </div>
  );
}

// -------- Page --------

export default function Feedback() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('editor');
  const [grouping, setGrouping] = useState('editor');
  const [showResolved, setShowResolved] = useState(false);
  const [search, setSearch] = useState('');
  const [busyIds, setBusyIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/dev/feedback');
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markBusy = (id, busy) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleResolved = async (kind, row) => {
    const id = row._id;
    markBusy(id, true);
    try {
      const path =
        kind === 'editor'
          ? `/dev/feedback/editor-feedback/${id}`
          : `/dev/feedback/problem-reports/${id}`;
      await api.patch(path, { resolved: !row.resolved });
      // Optimistic local patch.
      setData((prev) => {
        if (!prev) return prev;
        const key = kind === 'editor' ? 'editorFeedback' : 'problemReports';
        return {
          ...prev,
          [key]: prev[key].map((r) =>
            r._id === id ? { ...r, resolved: !r.resolved, resolvedAt: !r.resolved ? new Date().toISOString() : null } : r
          ),
        };
      });
    } catch (err) {
      setError(err.message);
    } finally {
      markBusy(id, false);
    }
  };

  const activeRows = useMemo(() => {
    if (!data) return [];
    if (tab === 'editor') return data.editorFeedback;
    if (tab === 'problem') return data.problemReports;
    return data.articleFeedback;
  }, [data, tab]);

  const filteredRows = useMemo(() => {
    let rows = activeRows;
    if (!showResolved && (tab === 'editor' || tab === 'problem')) {
      rows = rows.filter((r) => !r.resolved);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const blob = [
          r.message,
          r.description,
          r.q1Label,
          r.branchLabel,
          r.branchAnswer,
          r.followupLabel,
          r.followupAnswer,
          r.editor?.name,
          r.editor?.email,
          r.article?.headline,
          r.type,
          r.category,
          r.branch,
          r.q1Answer,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return blob.includes(q);
      });
    }
    return rows;
  }, [activeRows, showResolved, search, tab]);

  const groups = useMemo(
    () => groupRows(filteredRows, grouping, editorKey, dateKey),
    [filteredRows, grouping]
  );

  const renderRow = (row) => {
    if (tab === 'editor') {
      return (
        <EditorFeedbackRow
          key={row._id}
          row={row}
          busy={busyIds.has(row._id)}
          onToggleResolved={() => toggleResolved('editor', row)}
        />
      );
    }
    if (tab === 'problem') {
      return (
        <ProblemReportRow
          key={row._id}
          row={row}
          busy={busyIds.has(row._id)}
          onToggleResolved={() => toggleResolved('problem', row)}
        />
      );
    }
    return <ArticleFeedbackRow key={row._id} row={row} />;
  };

  const counts = data?.counts || {};

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/admin/dev"
        className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
      >
        ← Back to Dev Dashboard
      </Link>
      <div className="flex items-start justify-between gap-4 mb-6 mt-4 flex-wrap">
        <div>
          <h1 className="hxd-page-title">Feedback</h1>
          <p className="hxd-page-subtitle">
            Everything editors have sent from the editor dashboard, in one place.
          </p>
        </div>
        <button className="hxd-btn hxd-btn-ghost hxd-btn-sm" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="mb-5 flex items-start justify-between gap-3 bg-red-50 border border-red-200 rounded p-3">
          <p className="text-hxd-err text-[13px]">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-hxd-muted hover:text-hxd-err text-lg leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b-2 border-hxd-orange flex-wrap">
        {TABS.map((t) => {
          const active = tab === t.key;
          const count =
            t.key === 'editor'
              ? counts.editorFeedback
              : t.key === 'problem'
              ? counts.problemReports
              : counts.articleFeedback;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                'px-3 py-2 font-display text-[11px] font-bold uppercase tracking-display-md rounded-t-[3px] transition-colors ' +
                (active
                  ? 'bg-hxd-orange text-white'
                  : 'bg-hxd-surface text-hxd-muted hover:text-hxd-ink')
              }
            >
              {t.label}
              {typeof count === 'number' && (
                <span className={'ml-2 ' + (active ? 'opacity-80' : 'text-hxd-muted')}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-[12px] text-hxd-muted mb-4">
        {TABS.find((t) => t.key === tab)?.hint}
      </p>

      {/* Controls */}
      <div className="hxd-card mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[180px]">
          <label className="hxd-label">Search</label>
          <input
            className="hxd-input"
            placeholder="message, editor, headline…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="hxd-label">Group by</label>
          <div className="flex gap-1">
            {GROUPINGS.map((g) => {
              const active = grouping === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGrouping(g.key)}
                  className={
                    'px-3 py-1.5 rounded-[3px] border text-[12px] font-bold ' +
                    (active
                      ? 'bg-hxd-orange text-white border-hxd-orange'
                      : 'bg-white text-hxd-ink border-hxd-border hover:border-hxd-orange')
                  }
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>
        {(tab === 'editor' || tab === 'problem') && (
          <label className="flex items-center gap-2 text-[12px] text-hxd-ink select-none">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            Show resolved
          </label>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <p className="text-hxd-muted text-[13px]">Loading feedback…</p>
      ) : filteredRows.length === 0 ? (
        <div className="hxd-card">
          <p className="text-hxd-muted text-[13px]">
            No feedback matches the current filters.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key} className="hxd-card p-0 overflow-hidden">
              <div className="hxd-card-header px-4 pt-3 pb-2 flex items-center justify-between border-b border-hxd-border-soft mb-0">
                <span>
                  {grouping === 'date' && g.key !== 'undated' ? fmtDate(g.label) : g.label}
                </span>
                <span className="text-hxd-muted normal-case tracking-normal font-normal">
                  {g.rows.length} {g.rows.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div>{g.rows.map(renderRow)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
